import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiRegister } from '../../app/auth/authApi';
import { LiftXLogo } from '../../ui/components/LiftXLogo';
import { CompanyMark } from '../../ui/components/CompanyMark';
import { COMPANY_NAME } from '../../app/appInfo';
import { ROUTES } from '../../ui/navigation/routes';
// Reuses LoginScreen's own CSS module rather than a near-duplicate file —
// both are the same full-screen auth-card shape, same design system.
import styles from './LoginScreen.module.css';

/** Must match MIN_PASSWORD_LENGTH in functions/api/_lib/http.ts — the
 *  server is still the real enforcement boundary; this is display copy. */
const MIN_PASSWORD_LENGTH = 8;

/**
 * Self-service registration. Registration + Approval plan.
 *
 * Deliberately never authenticates on success — the server creates the
 * account as 'pending' and issues no session cookie (register.ts). This
 * screen's only job after a successful submit is to say so plainly and
 * hand the user back to Login; there is no "logged in but restricted"
 * state to render here, because no session exists yet at all.
 */
export function RegisterScreen(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  const emailId = useId();
  const passwordId = useId();
  const confirmId = useId();
  const errorId = useId();
  const hintId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting) return; // guards against a double-tap firing two requests

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${String(MIN_PASSWORD_LENGTH)} characters.`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const result = await apiRegister(email, password, confirmPassword);
      if (result.outcome === 'success') {
        setRegisteredEmail(result.email);
      } else if (result.outcome === 'email-taken') {
        setError('An account with this email address already exists.');
      } else if (result.outcome === 'network-error') {
        setError('Can’t reach the server. Check your connection and try again.');
      } else {
        // Server-side validation rejected the request (bad email shape,
        // password too short, mismatch) — client-side checks above already
        // catch the common cases, but the server is the real boundary.
        setError('Could not create your account. Check your details and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (registeredEmail !== null) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.logoWrap}>
            <LiftXLogo height={96} />
          </div>
          <h1 className={styles.title}>Registration successful</h1>
          <p className={styles.subtitle} role="status">
            We&apos;ve sent a verification email to your email address — feel free to confirm it, but
            it isn&apos;t required right now. Your account is awaiting review by an administrator;
            you&apos;ll be able to sign in once it&apos;s approved.
          </p>
          <Link to={ROUTES.dashboard} className={styles.submit}>
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className={styles.logoWrap}>
          <LiftXLogo height={96} />
        </div>

        <h1 className={styles.title}>Create account</h1>
        <p className={styles.subtitle}>
          <CompanyMark size={14} className={styles.subtitleMark} />
          Personal access · {COMPANY_NAME}
        </p>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={emailId}>
            Email
          </label>
          <input
            id={emailId}
            className={styles.input}
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={submitting}
            required
            aria-invalid={error !== null}
            aria-describedby={error !== null ? errorId : undefined}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={passwordId}>
            Password
          </label>
          <input
            id={passwordId}
            className={styles.input}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={submitting}
            required
            aria-describedby={hintId}
          />
          <p id={hintId} className={styles.note}>
            At least {MIN_PASSWORD_LENGTH} characters.
          </p>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={confirmId}>
            Confirm password
          </label>
          <input
            id={confirmId}
            className={styles.input}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            disabled={submitting}
            required
            aria-invalid={error !== null}
            aria-describedby={error !== null ? errorId : undefined}
          />
        </div>

        {error !== null && (
          <p id={errorId} role="alert" className={styles.error}>
            {error}
          </p>
        )}

        <button type="submit" className={styles.submit} disabled={submitting}>
          {submitting ? 'Creating account…' : 'Create Account'}
        </button>

        <p className={styles.registerPrompt}>
          Already have an account?{' '}
          <Link to={ROUTES.dashboard} className={styles.registerLink}>
            Log in
          </Link>
        </p>
      </form>
    </div>
  );
}
